import { useContext, useSignal, useComputedQrl, inlinedQrl, useLexicalScope, useTaskQrl } from "@builder.io/qwik";
import { isServer, isBrowser } from "@builder.io/qwik/build";
import { dropdownContextId } from "./dropdown-context.qwik.mjs";
import { useDropdown } from "./use-dropdown.qwik.mjs";
function useDropdownItem(props) {
  const context = useContext(dropdownContextId);
  const { getNextEnabledItemIndex$, getPrevEnabledItemIndex$ } = useDropdown();
  const { _index, disabled, closeOnSelect = true } = props;
  const itemRef = useSignal();
  const localIndexSig = useSignal(null);
  const itemId = `${context.localId}-${_index}`;
  const isHighlightedSig = useComputedQrl(/* @__PURE__ */ inlinedQrl(() => {
    const [_index2, context2, disabled2, itemRef2] = useLexicalScope();
    if (disabled2)
      return;
    if (context2.highlightedIndexSig.value === _index2) {
      itemRef2.value?.focus();
      return true;
    } else
      return false;
  }, "useDropdownItem_isHighlightedSig_useComputed_0tD1NGtgwLA", [
    _index,
    context,
    disabled,
    itemRef
  ]));
  useTaskQrl(/* @__PURE__ */ inlinedQrl(async function getIndexTask() {
    const [_index2, localIndexSig2] = useLexicalScope();
    if (_index2 === void 0)
      throw Error("Qwik UI: Dropdown component item cannot find its proper index.");
    localIndexSig2.value = _index2;
  }, "useDropdownItem_useTask_ngAED40tnU4", [
    _index,
    localIndexSig
  ]));
  useTaskQrl(/* @__PURE__ */ inlinedQrl(async function navigationTask({ track, cleanup }) {
    const [context2, isHighlightedSig2, itemRef2, localIndexSig2] = useLexicalScope();
    track(() => context2.highlightedIndexSig.value);
    if (localIndexSig2.value === context2.highlightedIndexSig.value)
      context2.highlightedItemRef = itemRef2;
    if (isServer)
      return;
    let observer;
    const checkVisibility = (entries) => {
      const [entry] = entries;
      if (isHighlightedSig2.value && !entry.isIntersecting)
        itemRef2.value?.scrollIntoView(context2.scrollOptions);
    };
    cleanup(() => observer?.disconnect());
    if (isBrowser) {
      observer = new IntersectionObserver(checkVisibility, {
        root: context2.contentRef.value,
        threshold: 1
      });
      if (itemRef2.value)
        observer.observe(itemRef2.value);
    }
  }, "useDropdownItem_useTask_1_G07uFGFY56E", [
    context,
    isHighlightedSig,
    itemRef,
    localIndexSig
  ]));
  const handleClick$ = /* @__PURE__ */ inlinedQrl(async () => {
    const [closeOnSelect2, context2, disabled2, localIndexSig2, props2] = useLexicalScope();
    if (disabled2 || localIndexSig2.value === null)
      return;
    props2.onItemSelect?.();
    if (closeOnSelect2) {
      context2.isOpenSig.value = false;
      context2.triggerRef.value?.focus();
    }
  }, "useDropdownItem_handleClick_IOmWD0hVDmU", [
    closeOnSelect,
    context,
    disabled,
    localIndexSig,
    props
  ]);
  const handlePointerOver$ = /* @__PURE__ */ inlinedQrl(() => {
    const [context2, disabled2, localIndexSig2] = useLexicalScope();
    if (disabled2)
      return;
    if (localIndexSig2.value !== null)
      context2.highlightedIndexSig.value = localIndexSig2.value;
  }, "useDropdownItem_handlePointerOver_0eS1T69jW0U", [
    context,
    disabled,
    localIndexSig
  ]);
  const handleKeyDown$ = /* @__PURE__ */ inlinedQrl(async (e) => {
    const [closeOnSelect2, context2, getNextEnabledItemIndex$2, getPrevEnabledItemIndex$2, props2] = useLexicalScope();
    switch (e.key) {
      case "ArrowDown":
        if (context2.isOpenSig.value)
          context2.highlightedIndexSig.value = await getNextEnabledItemIndex$2(context2.highlightedIndexSig.value);
        break;
      case "ArrowUp":
        if (context2.isOpenSig.value)
          context2.highlightedIndexSig.value = await getPrevEnabledItemIndex$2(context2.highlightedIndexSig.value);
        break;
      case "Home":
        if (context2.isOpenSig.value)
          context2.highlightedIndexSig.value = await getNextEnabledItemIndex$2(-1);
        break;
      case "End":
        if (context2.isOpenSig.value) {
          const lastEnabledOptionIndex = await getPrevEnabledItemIndex$2(context2.itemsMapSig.value.size);
          context2.highlightedIndexSig.value = lastEnabledOptionIndex;
        }
        break;
      case "Escape":
        context2.triggerRef.value?.focus();
        context2.isOpenSig.value = false;
        break;
      case "Tab":
        context2.isOpenSig.value = false;
        break;
      case "Enter":
      case " ":
        if (context2.isOpenSig.value) {
          props2.onItemSelect?.();
          if (closeOnSelect2) {
            context2.isOpenSig.value = false;
            context2.triggerRef.value?.focus();
          }
        }
        break;
    }
  }, "useDropdownItem_handleKeyDown_69WXJ0ra1NI", [
    closeOnSelect,
    context,
    getNextEnabledItemIndex$,
    getPrevEnabledItemIndex$,
    props
  ]);
  return {
    handleKeyDown$,
    handleClick$,
    handlePointerOver$,
    itemId,
    itemRef,
    isHighlightedSig
  };
}
export {
  useDropdownItem
};
